/**
 * One document, one indexable URL.
 *
 * Every store product and the free store hub is a `content_posts` row of kind
 * "guide", so the same document renders at BOTH `/guides/<slug>` and
 * `/store/<slug>` — same title, same meta description, same body, and each URL
 * canonicalising to itself. Google then has to choose, and when it chooses the
 * /guides twin the submitted /store URL reports back as "Duplicate, Google
 * chose a different canonical than the user" and leaves the index.
 *
 * /store/<slug> is the survivor: it carries the price, the Product schema and
 * the buy panel, and StoreReader serves the entitled body to a buyer exactly
 * as the guides page did. So the guides twin 301s.
 *
 * Kept as a pure function so the rule is exercised directly by tests rather
 * than restated in them.
 */

/**
 * Free editorial hub that anchors the store (top of funnel). Not premium, but
 * store-owned: it renders at /store/<slug> as well. Defined here rather than in
 * store.functions.ts so this rule has no server-function dependency and can be
 * unit-tested offline; store.functions.ts re-exports it.
 */
export const STORE_HUB_SLUG = "start-a-cleaning-business-uk";

/** Store-owned slugs that are not premium. */
export const STORE_OWNED_FREE_SLUGS: readonly string[] = [STORE_HUB_SLUG];

export interface StoreTwinInput {
  /** `is_premium` on the content_posts row. */
  isPremium: boolean;
  /** Slugs that /store/<slug> actually renders a page for (products + hub). */
  storeSlugs: Iterable<string>;
}

/**
 * The path `/guides/<slug>` should 301 to, or null to serve the guide.
 *
 * Returns null when the slug has no store page — a premium post with no
 * `stripe_price_id` is not sellable, gets no /store page, and redirecting it
 * would send readers to a 404.
 */
export function storeTwinRedirect(slug: string, input: StoreTwinInput): string | null {
  const owned = input.isPremium || STORE_OWNED_FREE_SLUGS.includes(slug);
  if (!owned) return null;
  for (const s of input.storeSlugs) {
    if (s === slug) return `/store/${slug}`;
  }
  return null;
}
